import React from "react";
import clsx from "clsx";
import { createUseStyles } from "react-jss";
import Color from "color";

const stepStyleDefaults = {
  activeBgColor: "#ff6803",
  activeTextColor: "#ffffff",
  completedBgColor: "#ff6803",
  completedTextColor: "#ffffff",
  inactiveBgColor: "#e0e0e0",
  inactiveTextColor: "#ffffff",
  size: "1em",
  circleFontSize: "1rem",
  borderRadius: "50%",
};

const HOVER_COLOR_DEFAULT_RATIO = 0.15;
const ACTIVE_COLOR_DEFAULT_RATIO = 0.2;

const shadeOrTintColor = (colorString, ratio) => {
  const color = Color(colorString);
  return color.isDark()
    ? color.darken(ratio).hex()
    : color.lighten(ratio).hex();
};

const useStepStyles = createUseStyles({
  StepMain: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    width: "500px",
    padding: "20px",
    "@media (max-width: 600px)": {
      width: "100%",
    },
  },
  StepButton: (props = stepStyleDefaults) => ({
    border: "none",
    margin: 0,
    padding: 0,
    cursor: "pointer",
    borderRadius: props.borderRadius || stepStyleDefaults.borderRadius,
    backgroundColor: props.inactiveBgColor || stepStyleDefaults.inactiveBgColor,
    width: props.size || stepStyleDefaults.size,
    height: props.size || stepStyleDefaults.size,
    fontSize: "1em",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    "&.active": {
      backgroundColor: props.activeBgColor || stepStyleDefaults.activeBgColor,
      "&:hover": {
        backgroundColor: shadeOrTintColor(
          props.activeBgColor || stepStyleDefaults.activeBgColor,
          HOVER_COLOR_DEFAULT_RATIO
        ),
      },
      "&:active": {
        backgroundColor: shadeOrTintColor(
          props.activeBgColor || stepStyleDefaults.activeBgColor,
          ACTIVE_COLOR_DEFAULT_RATIO
        ),
      },
    },
    "&.completed": {
      backgroundColor:
        props.completedBgColor || stepStyleDefaults.completedBgColor,
      "&:hover": {
        backgroundColor: shadeOrTintColor(
          props.completedBgColor || stepStyleDefaults.completedBgColor,
          HOVER_COLOR_DEFAULT_RATIO
        ),
      },
      "&:active": {
        backgroundColor: shadeOrTintColor(
          props.completedBgColor || stepStyleDefaults.completedBgColor,
          ACTIVE_COLOR_DEFAULT_RATIO
        ),
      },
    },
  }),
  StepButtonContent: (props = stepStyleDefaults) => ({
    color: props.inactiveTextColor || stepStyleDefaults.inactiveTextColor,
    fontSize: props.circleFontSize || stepStyleDefaults.circleFontSize,
    fontVariantNumeric: "tabular-nums",
    "&.active": {
      color: props.activeTextColor || stepStyleDefaults.activeTextColor,
    },
    "&.completed": {
      color: props.completedTextColor || stepStyleDefaults.completedTextColor,
    },
  }),
  Connector: {
    flexGrow: 1,
    height: "2px",
    backgroundColor: stepStyleDefaults.inactiveBgColor,
    transition: "background-color 0.3s ease",
    minWidth: "30px",
    "&.completed": {
      backgroundColor: stepStyleDefaults.completedBgColor,
    },
  },
});

const ProgressBar = ({
  steps,
  className = "",
  stepClassName = "",
  activeStep = 0,
  hideConnectors = false,
  nonLinear = false,
  ...rest
}) => {
  const classes = useStepStyles();

  return (
    <div className={clsx(classes.StepMain, className)} {...rest}>
      {steps.map((step, index) => {
        const isActive = activeStep === index;
        const isCompleted = !nonLinear && activeStep > index;
        const isDisabled = !nonLinear && activeStep < index;

        return (
          <React.Fragment key={index}>
            <button
              className={clsx(
                classes.StepButton,
                {
                  active: isActive,
                  completed: isCompleted,
                  disabled: isDisabled,
                },
                stepClassName
              )}
              disabled={isDisabled}
            >
              <span
                className={clsx(classes.StepButtonContent, {
                  active: isActive,
                  completed: isCompleted,
                })}
              >
                {/* {step.label || index + 1} */}
              </span>
            </button>
            {index < steps.length - 1 && !hideConnectors && (
              <div
                className={clsx(classes.Connector, {
                  completed: activeStep > index,
                })}
              />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
};

export default ProgressBar;
